<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive } from "vue"
import { EyeIcon } from "@heroicons/vue/24/outline"
import AppShell from "../Layout/AppShell.vue"
import { UiDataTable } from "@/components"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"

type StockItemRow = {
  product_id: number
  company: string
  product: string
  unit: string
  current_quantity: string
  inbound_quantity: string
}

type SortState = {
  key: string
  direction: "asc" | "desc"
}

const props = defineProps<{
  stock_items: StockItemRow[]
  companies: Array<{ id: number; name: string }>
  movement_types: Array<{ label: string; value: string }>
  inbound_options: Array<{ label: string; value: string }>
  query: {
    q?: string
    company_id?: string
    movement_type?: string
    with_inbound?: string
    sort_by?: string
    sort_direction?: "asc" | "desc"
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "company", label: "Empresa", sortable: true },
  { key: "product", label: "Produto", sortable: true },
  { key: "unit", label: "Unidade", align: "center" },
  { key: "current_quantity", label: "Qtd. atual", align: "right", sortable: true },
  { key: "inbound_quantity", label: "Previsao entrada", align: "right", sortable: true },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
  {
    key: "movement_type",
    label: "Tipo de movimentacao",
    options: props.movement_types,
  },
  {
    key: "with_inbound",
    label: "Previsao de entrada",
    options: props.inbound_options.filter((option) => option.value !== ""),
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
  movement_type: props.query.movement_type ?? "",
  with_inbound: props.query.with_inbound ?? "",
})

const currentSort = reactive<SortState>({
  key: props.query.sort_by ?? "product",
  direction: props.query.sort_direction ?? "asc",
})

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    movement_type: String(overrides.movement_type ?? selectedFilters.movement_type ?? ""),
    with_inbound: String(overrides.with_inbound ?? selectedFilters.with_inbound ?? ""),
    sort_by: String(overrides.sort_by ?? currentSort.key ?? "product"),
    sort_direction: String(overrides.sort_direction ?? currentSort.direction ?? "asc"),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/stock_movements", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

function updateSort(sort: SortState) {
  currentSort.key = sort.key
  currentSort.direction = sort.direction
  fetchWithQuery({ sort_by: sort.key, sort_direction: sort.direction, page: 1 })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Estoque</h1>
        <p class="mt-2 text-sm text-slate-600">Veja o saldo atual por item e a previsao de entrada com base nos pedidos de compra pendentes.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="stock_items"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :sort="currentSort"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @update:sort="updateSort"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/stock_movements/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Nova movimentacao
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <Link
              :href="`/stock_movements/product_movements?product_id=${(row as StockItemRow).product_id}&company_id=${query.company_id ?? ''}`"
              class="inline-flex items-center rounded-md border border-slate-300 p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Visualizar movimentacoes do item"
              title="Visualizar movimentacoes do item"
            >
              <EyeIcon class="size-4" />
            </Link>
          </div>
          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>
    </div>
  </AppShell>
</template>

